import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateApiKeyRequest,
    PartnerPermission,
} from '@/lib/api/api-key-api'
import { apiKeyApi } from '@/lib/api/api-key-api'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'

export const apiKeyKeys = {
    all: ['api-keys'] as const,
    lists: () => [...apiKeyKeys.all, 'list'] as const,
}

export function useApiKeys() {
    return useQuery({
        queryKey: apiKeyKeys.lists(),
        queryFn: async () => {
            const res = await apiKeyApi.list()
            return res.data ?? []
        },
    })
}

// useCreateApiKey returns the created key (including the one-time plaintext) so
// the caller can reveal it. Toast is left to the caller — the reveal modal is
// the real feedback.
export function useCreateApiKey() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (data: CreateApiKeyRequest) => {
            const res = await apiKeyApi.create(data)
            return res.data
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: apiKeyKeys.lists() })
        },
        onError: (err) =>
            toast.error(
                (err instanceof ApiError &&
                    resolveApiErrorMessage(err.code, err.params)) ||
                    m.api_keys_create_error(),
            ),
    })
}

export function useRevokeApiKey() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => apiKeyApi.revoke(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: apiKeyKeys.lists() })
            toast.success(m.api_keys_revoke_success())
        },
        onError: () => toast.error(m.api_keys_revoke_error()),
    })
}

// useUpdateApiKeyPermissions thay quyền của một khoá partner. Lỗi có mã (thiếu
// quyền, khoá đã thu hồi…) hiện câu riêng; còn lại dùng câu chung.
export function useUpdateApiKeyPermissions() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (vars: {
            id: string
            permissions: PartnerPermission[]
        }) => {
            const res = await apiKeyApi.updatePermissions(
                vars.id,
                vars.permissions,
            )
            return res.data
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: apiKeyKeys.lists() })
            toast.success(m.api_keys_update_success())
        },
        onError: (err) =>
            toast.error(
                (err instanceof ApiError &&
                    resolveApiErrorMessage(err.code, err.params)) ||
                    m.api_keys_update_error(),
            ),
    })
}
